import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Modal, Row, Table } from 'react-bootstrap';
import { useModal } from '../../../context/ModalContext';
import type { User } from '../../../types';
import type { ChangeWorkGroup } from '../../../types/changes';
import { immediateManagerChoicesForMember } from '../changeWorkGroupImmediateManagerChoices';

interface Props {
  group: ChangeWorkGroup | null;
  users: User[];
  canManage: boolean;
  onHide: () => void;
  onAddMember: (userId: number, isManager: boolean) => Promise<boolean> | boolean | void;
  onAddAllSystemUsers?: (
    isManager: boolean,
  ) => Promise<{ added: number; skipped: number }> | { added: number; skipped: number };
  onSetMemberManager: (userId: number, isManager: boolean) => Promise<boolean> | boolean | void;
  onSetMemberImmediateManager?: (
    userId: number,
    immediateManagerUserId: number | null,
  ) => Promise<boolean> | boolean | void;
  onAddPerson: (name: string, email: string) => Promise<boolean> | boolean | void;
  onRemoveMember: (userId: number, name: string) => Promise<boolean> | boolean | void;
}

const ChangeWorkGroupPeopleModal: React.FC<Props> = ({
  group,
  users,
  canManage,
  onHide,
  onAddMember,
  onAddAllSystemUsers,
  onSetMemberManager,
  onSetMemberImmediateManager,
  onAddPerson,
  onRemoveMember,
}) => {
  const { showConfirm, showAlert } = useModal();
  const [memberId, setMemberId] = useState('');
  const [includeAllUsers, setIncludeAllUsers] = useState(false);
  const [addingMembers, setAddingMembers] = useState(false);
  const [newAsManager, setNewAsManager] = useState(false);
  const [personName, setPersonName] = useState('');
  const [personEmail, setPersonEmail] = useState('');

  const members = group?.members || [];
  const contacts = group?.contacts || [];
  const groupName = group?.name || '';

  useEffect(() => {
    setMemberId('');
    setIncludeAllUsers(false);
    setAddingMembers(false);
    setNewAsManager(false);
  }, [group?.group_id]);

  const handleAddMember = async () => {
    if (includeAllUsers && onAddAllSystemUsers) {
      setAddingMembers(true);
      try {
        const result = await onAddAllSystemUsers(newAsManager);
        showAlert({
          title: 'Usuários incluídos',
          message:
            result.added > 0
              ? `${result.added} usuário(s) incluído(s) no grupo.`
              : 'Nenhum usuário novo para incluir (todos já estavam no grupo ou a lista está vazia).',
          variant: result.added > 0 ? 'success' : 'info',
        });
        setIncludeAllUsers(false);
        setNewAsManager(false);
      } finally {
        setAddingMembers(false);
      }
      return;
    }
    const userId = Number(memberId || 0);
    if (!userId) return;
    setAddingMembers(true);
    try {
      await onAddMember(userId, newAsManager);
      setMemberId('');
      setNewAsManager(false);
    } finally {
      setAddingMembers(false);
    }
  };

  const handleAddPerson = async () => {
    await onAddPerson(personName, personEmail);
    setPersonName('');
    setPersonEmail('');
  };

  const handleRemove = async (userId: number, name: string) => {
    const ok = await showConfirm({
      title: 'Remover pessoa',
      message: `Remover «${name}» do grupo «${groupName}»?`,
      details: 'A pessoa deixa de participar das aprovações deste grupo em Mudanças.',
      variant: 'danger',
      confirmLabel: 'Remover',
      cancelLabel: 'Cancelar',
    });
    if (!ok) return;
    await onRemoveMember(userId, name);
  };

  const availableUsers = users.filter(
    (entry) => !members.some((member) => member.user_id === entry.id),
  );

  return (
    <Modal
      show={Boolean(group)}
      onHide={onHide}
      backdrop="static"
      keyboard={false}
      scrollable
      size="lg"
      enforceFocus={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className="fs-5">Pessoas — {groupName}</Modal.Title>
      </Modal.Header>
      <Modal.Body className="pt-2">
        <Alert variant="light" className="border small py-2 mb-3">
          Membros valem só para <strong>Mudanças</strong>. Qualquer membro da área aprova no comitê
          (CAMUD/Riscos). Marque <strong>Gestor</strong> para Transporte/emergencial. Opcionalmente
          defina o <strong>gerente imediato</strong> individual (qualquer pessoa do grupo; gestor
          pode escolher a si mesmo). Não
          altera incidentes, compras nem PMO.
        </Alert>

        <h6 className="text-muted text-uppercase small mb-2">Membros ({members.length})</h6>
        {members.length === 0 ? (
          <p className="text-muted small mb-3">Nenhum membro cadastrado.</p>
        ) : (
          <Table responsive size="sm" className="align-middle mb-3">
            <thead className="table-light">
              <tr>
                <th>Nome</th>
                <th>E-mail</th>
                <th className="text-center" style={{ width: 100 }}>
                  Gestor
                </th>
                <th style={{ minWidth: 200 }}>Gerente imediato</th>
                {canManage ? <th className="text-end" style={{ width: 96 }} /> : null}
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.user_id}>
                  <td className="fw-medium">
                    {member.name || 'Sem nome'}
                    {member.is_manager ? (
                      <Badge bg="primary" className="ms-2">
                        Gestor
                      </Badge>
                    ) : null}
                  </td>
                  <td className="text-muted small">{member.email || '—'}</td>
                  <td className="text-center">
                    {canManage ? (
                      <Form.Check
                        type="switch"
                        id={`mgr-${group?.group_id}-${member.user_id}`}
                        label=""
                        aria-label={`Gestor — ${member.name || member.user_id}`}
                        checked={Boolean(member.is_manager)}
                        onChange={(e) => void onSetMemberManager(member.user_id, e.target.checked)}
                      />
                    ) : member.is_manager ? (
                      <span className="text-success small">Sim</span>
                    ) : (
                      <span className="text-muted small">—</span>
                    )}
                  </td>
                  <td>
                    {canManage && onSetMemberImmediateManager ? (
                      <Form.Select
                        size="sm"
                        aria-label={`Gerente imediato de ${member.name || member.user_id}`}
                        value={
                          member.immediate_manager_user_id != null
                            ? String(member.immediate_manager_user_id)
                            : ''
                        }
                        onChange={(e) => {
                          const raw = e.target.value;
                          void onSetMemberImmediateManager(
                            member.user_id,
                            raw ? Number(raw) : null,
                          );
                        }}
                      >
                        <option value="">Padrão (gestor do grupo)</option>
                        {immediateManagerChoicesForMember(members, member).map((entry) => (
                          <option key={entry.user_id} value={String(entry.user_id)}>
                            {entry.name || entry.email || `#${entry.user_id}`}
                            {entry.is_manager ? ' · Gestor' : ''}
                            {entry.user_id === member.user_id ? ' (eu mesmo)' : ''}
                          </option>
                        ))}
                      </Form.Select>
                    ) : (
                      <span className="text-muted small">
                        {member.immediate_manager_name ||
                          member.immediate_manager_email ||
                          'Padrão (gestor do grupo)'}
                      </span>
                    )}
                  </td>
                  {canManage ? (
                    <td className="text-end">
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => void handleRemove(member.user_id, member.name || 'esta pessoa')}
                      >
                        Remover
                      </Button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        {contacts.length > 0 ? (
          <>
            <h6 className="text-muted text-uppercase small mb-2">Contatos por e-mail ({contacts.length})</h6>
            <ul className="list-unstyled small mb-3">
              {contacts.map((contact) => (
                <li key={`contact-${contact.id}`} className="mb-1">
                  <span className="fw-medium">{contact.name}</span>{' '}
                  <span className="text-muted">({contact.email})</span>
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {canManage ? (
          <>
            <h6 className="text-muted text-uppercase small mb-2 mt-2">Incluir pessoa</h6>
            <Row className="g-3">
              <Col md={6}>
                <Card className="h-100 border">
                  <Card.Body className="py-3">
                    <Card.Title className="fs-6 mb-2">Usuário do sistema</Card.Title>
                    <Form.Check
                      type="checkbox"
                      id={`all-users-${group?.group_id || 'x'}`}
                      className="mb-2"
                      label="Incluir todos os usuários ativos do sistema (ainda não no grupo)"
                      checked={includeAllUsers}
                      disabled={addingMembers || !onAddAllSystemUsers}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setIncludeAllUsers(checked);
                        if (checked) setMemberId('');
                      }}
                    />
                    <Form.Select
                      className="mb-2"
                      value={memberId}
                      disabled={includeAllUsers || addingMembers}
                      onChange={(e) => setMemberId(e.target.value)}
                      aria-label={`Incluir pessoa em ${groupName}`}
                    >
                      <option value="">
                        {includeAllUsers ? 'Serão incluídos todos os usuários ativos…' : 'Selecione…'}
                      </option>
                      {availableUsers.map((entry) => (
                        <option key={entry.id} value={entry.id}>
                          {entry.name}
                          {entry.email ? ` (${entry.email})` : ''}
                        </option>
                      ))}
                    </Form.Select>
                    {!includeAllUsers && availableUsers.length === 0 ? (
                      <p className="small text-muted mb-2">Todos os usuários carregados já estão no grupo.</p>
                    ) : null}
                    <Form.Check
                      type="checkbox"
                      id={`new-mgr-${group?.group_id || 'x'}`}
                      className="mb-3"
                      label="Incluir como gestor"
                      checked={newAsManager}
                      disabled={addingMembers}
                      onChange={(e) => setNewAsManager(e.target.checked)}
                    />
                    <Button
                      size="sm"
                      disabled={addingMembers || (!includeAllUsers && !memberId)}
                      onClick={() => void handleAddMember()}
                    >
                      {addingMembers
                        ? 'Incluindo…'
                        : includeAllUsers
                          ? 'Incluir todos os usuários'
                          : 'Incluir usuário'}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card className="h-100 border">
                  <Card.Body className="py-3">
                    <Card.Title className="fs-6 mb-2">Por e-mail (contato)</Card.Title>
                    <Form.Control
                      className="mb-2"
                      placeholder="Nome"
                      aria-label={`Nome da pessoa em ${groupName}`}
                      value={personName}
                      onChange={(e) => setPersonName(e.target.value)}
                    />
                    <Form.Control
                      className="mb-3"
                      type="email"
                      placeholder="E-mail"
                      aria-label={`E-mail da pessoa em ${groupName}`}
                      value={personEmail}
                      onChange={(e) => setPersonEmail(e.target.value)}
                    />
                    <Button
                      size="sm"
                      variant="outline-primary"
                      disabled={personName.trim().length < 2 || !personEmail.trim()}
                      onClick={() => void handleAddPerson()}
                    >
                      Incluir por e-mail
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        ) : null}
      </Modal.Body>
      <Modal.Footer className="border-top-0 pt-0">
        <Button variant="outline-secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ChangeWorkGroupPeopleModal;
